import { useState, useEffect } from "react";
import { fetchBlob } from "../lib/api";
import { decrypt } from "../lib/crypto";
import { loadGroupKey, loadAuthToken } from "../lib/store";

export interface DecryptedImageState {
  url: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Turn an opaque ciphertext blob reference into a renderable object URL.
 *
 * `blobToken` is whatever the server hands back for fetching an encrypted image
 * blob — currently a signed Active Storage proxy URL from PhotosController /
 * MembersController. The bytes behind it are ciphertext, never a directly viewable
 * image, so this hook fetches them over an authenticated request (api.fetchBlob),
 * decrypts them with the family group key via crypto.decrypt, and wraps the
 * plaintext in a Blob + URL.createObjectURL.
 *
 * The object URL is revoked whenever the token changes or the component unmounts
 * (the useEffect cleanup), so a re-render with a new token never leaves the old
 * object URL alive — no blob URL is leaked.
 */
export function useDecryptedImage(blobToken: string | null | undefined): DecryptedImageState {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!!blobToken);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!blobToken) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    // Clear any prior url up front so a revoked object URL is never rendered while
    // the new token is being fetched and decrypted.
    setUrl(null);
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const auth = await loadAuthToken();
        const [groupKey, ciphertext] = await Promise.all([
          loadGroupKey(),
          fetchBlob(blobToken, auth),
        ]);
        const plaintext = await decrypt(groupKey, ciphertext);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([plaintext]));
        setUrl(objectUrl);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [blobToken]);

  return { url, loading, error };
}
